(function () {
  'use strict';

  var VERSION = '2026-06-19.2-sticky-buy-bar-bottom-fix';
  var BAR_SELECTOR = '[data-c2b-sticky-buy-bar]';
  var VISIBLE_CLASS = 'is-visible';
  var LOADING_CLASS = 'is-loading';
  var state = new Map();
  var namespace = window.C2BProductStickyBuyBar = window.C2BProductStickyBuyBar || {};

  if (typeof namespace.stop === 'function') {
    namespace.stop();
  }

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function safeQuery(root, selector) {
    try {
      return root && selector ? root.querySelector(selector) : null;
    } catch (error) {
      return null;
    }
  }

  function getBars(root) {
    var scope = root && root.querySelectorAll ? root : document;

    if (scope.matches && scope.matches(BAR_SELECTOR)) {
      return [scope];
    }

    return Array.prototype.slice.call(scope.querySelectorAll(BAR_SELECTOR));
  }

  function getProductInfo(bar) {
    return bar && bar.closest ? bar.closest('product-info') : null;
  }

  function getHeaderOffset() {
    var selectors = ['sticky-header', '.shopify-section-header-sticky', '#shopify-section-header'];
    var offset = 0;

    selectors.forEach(function (selector) {
      var element = safeQuery(document, selector);
      var style;
      var rect;

      if (!element) {
        return;
      }

      style = window.getComputedStyle(element);

      if (style.display === 'none' || style.visibility === 'hidden') {
        return;
      }

      rect = element.getBoundingClientRect();

      if ((style.position === 'fixed' || style.position === 'sticky') && rect.top <= 5 && rect.bottom > 0) {
        offset = Math.max(offset, Math.round(rect.bottom));
      }
    });

    return offset;
  }

  function getPriceText(priceRoot) {
    var selectors = [
      '.price-item--sale',
      '.price__regular .price-item--regular',
      '.price-item--regular',
      '.price'
    ];
    var index;
    var node;
    var text;

    if (!priceRoot) {
      return '';
    }

    for (index = 0; index < selectors.length; index += 1) {
      node = safeQuery(priceRoot, selectors[index]);

      if (!node) {
        continue;
      }

      text = normalizeText(node.textContent || '');

      if (text) {
        return text;
      }
    }

    return normalizeText(priceRoot.textContent || '');
  }

  function syncPrice(record) {
    var text;

    if (!record || !record.priceTarget || !record.priceSource) {
      return;
    }

    text = getPriceText(record.priceSource);

    if (text) {
      record.priceTarget.textContent = text;
    }
  }

  function getButtonText(button) {
    var span = button ? button.querySelector('span') : null;

    return normalizeText(span ? span.textContent : button && button.textContent);
  }

  function syncButton(record) {
    var disabled;
    var text;

    if (!record || !record.realButton || !record.stickyButton) {
      return;
    }

    disabled = record.realButton.disabled || record.realButton.getAttribute('aria-disabled') === 'true';
    text = getButtonText(record.realButton);

    record.stickyButton.disabled = disabled;

    if (disabled) {
      record.stickyButton.setAttribute('aria-disabled', 'true');
    } else {
      record.stickyButton.removeAttribute('aria-disabled');
    }

    record.stickyButton.classList.toggle(LOADING_CLASS, record.realButton.classList.contains('loading'));

    if (text && record.stickyButtonText) {
      record.stickyButtonText.textContent = text;
    }
  }

  function updateVisibility(record) {
    var headerOffset;
    var targetRect;
    var shouldShow;

    if (!record || !record.bar || !record.observeTarget) {
      return;
    }

    headerOffset = getHeaderOffset();
    record.bar.style.setProperty('--c2b-sticky-buy-top', headerOffset + 'px');

    targetRect = record.observeTarget.getBoundingClientRect();

    /*
      Keep the sticky buy bar visible after the original CTA has scrolled above
      the sticky/header area. Do not hide it at the end of the product section:
      related products, footer, and long product content can appear after the
      product-info wrapper while the purchase shortcut should remain available.
    */
    shouldShow = targetRect.bottom <= headerOffset + 4;

    record.bar.classList.toggle(VISIBLE_CLASS, shouldShow);
    record.bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  function schedule(record) {
    if (!record || record.raf) {
      return;
    }

    record.raf = window.requestAnimationFrame(function () {
      record.raf = null;
      syncButton(record);
      syncPrice(record);
      updateVisibility(record);
    });
  }

  function destroyBar(bar) {
    var record = state.get(bar);

    if (!record) {
      return;
    }

    if (record.intersectionObserver) {
      record.intersectionObserver.disconnect();
    }

    if (record.buttonObserver) {
      record.buttonObserver.disconnect();
    }

    if (record.priceObserver) {
      record.priceObserver.disconnect();
    }

    if (record.raf) {
      window.cancelAnimationFrame(record.raf);
    }

    window.removeEventListener('scroll', record.onScroll, { passive: true });
    window.removeEventListener('resize', record.onResize);
    window.removeEventListener('pageshow', record.onPageShow);

    state.delete(bar);
  }

  function initBar(bar) {
    var productInfo;
    var formId;
    var form;
    var submitSelector;
    var priceSelector;
    var realButton;
    var stickyButton;
    var observeTarget;
    var record;

    if (!bar || state.has(bar)) {
      return;
    }

    productInfo = getProductInfo(bar);
    formId = bar.getAttribute('data-product-form-id');
    form = formId ? document.getElementById(formId) : null;
    submitSelector = bar.getAttribute('data-product-submit-selector');
    priceSelector = bar.getAttribute('data-product-price-selector');
    realButton = submitSelector ? safeQuery(productInfo || document, submitSelector) : null;
    realButton = realButton || (form ? form.querySelector('[type="submit"]') : null);
    stickyButton = safeQuery(bar, '[data-c2b-sticky-buy-submit]');
    observeTarget = realButton ? realButton.closest('.product-form__buttons') || realButton : null;

    if (!form || !realButton || !stickyButton || !observeTarget) {
      return;
    }

    if (formId && stickyButton.getAttribute('form') !== formId) {
      stickyButton.setAttribute('form', formId);
    }

    record = {
      bar: bar,
      productInfo: productInfo,
      form: form,
      realButton: realButton,
      stickyButton: stickyButton,
      stickyButtonText: safeQuery(stickyButton, '[data-c2b-sticky-buy-submit-text]'),
      priceSource: priceSelector ? safeQuery(productInfo || document, priceSelector) : null,
      priceTarget: safeQuery(bar, '[data-c2b-sticky-buy-price]'),
      observeTarget: observeTarget,
      raf: null,
      onScroll: null,
      onResize: null,
      onPageShow: null,
      intersectionObserver: null,
      buttonObserver: null,
      priceObserver: null
    };

    record.onScroll = function () {
      schedule(record);
    };

    record.onResize = function () {
      schedule(record);
    };

    record.onPageShow = function () {
      schedule(record);
    };

    state.set(bar, record);

    bar.hidden = false;
    bar.setAttribute('data-c2b-sticky-buy-version', VERSION);
    bar.setAttribute('aria-hidden', 'true');

    if (typeof IntersectionObserver !== 'undefined') {
      record.intersectionObserver = new IntersectionObserver(function () {
        schedule(record);
      }, {
        threshold: [0, 0.01, 1]
      });
      record.intersectionObserver.observe(observeTarget);
    }

    if (typeof MutationObserver !== 'undefined') {
      record.buttonObserver = new MutationObserver(function () {
        schedule(record);
      });
      record.buttonObserver.observe(realButton, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class']
      });

      if (record.priceSource) {
        record.priceObserver = new MutationObserver(function () {
          schedule(record);
        });
        record.priceObserver.observe(record.priceSource, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    }

    window.addEventListener('scroll', record.onScroll, { passive: true });
    window.addEventListener('resize', record.onResize);
    window.addEventListener('pageshow', record.onPageShow);

    schedule(record);
  }

  function init(root) {
    getBars(root).forEach(initBar);
  }

  function stop() {
    Array.from(state.keys()).forEach(destroyBar);
  }

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  }

  namespace.version = VERSION;
  namespace.init = init;
  namespace.stop = stop;

  ready(function () {
    init(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    getBars(event.target).forEach(destroyBar);
  });
}());
